// src/components/LoadingState.js
import React from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    useTheme,
    Avatar,
    Fade,
    Zoom
} from '@mui/material';
import {
    LunchDining,
    Restaurant,
    Fastfood,
    LocalDining
} from '@mui/icons-material';

const LoadingState = ({ 
    message = "Loading Lunch Welfare Data...", 
    icon = null,
    fullPage = false,
    size = "medium"
}) => {
    const theme = useTheme();

    // Get primary color - using bag color theme
    const primaryColor = '#8B4513'; // Saddle brown - bag color
    const primaryLight = '#A0522D'; // Sienna - lighter brown
    const secondaryColor = '#D2691E'; // Chocolate - accent color

    // Size mappings
    const sizeMap = {
        small: {
            iconSize: 40,
            fontSize: 'h6',
            progressHeight: 4,
            containerHeight: fullPage ? '100vh' : '200px'
        },
        medium: {
            iconSize: 60,
            fontSize: 'h5',
            progressHeight: 6,
            containerHeight: fullPage ? '100vh' : '300px'
        },
        large: {
            iconSize: 80,
            fontSize: 'h4',
            progressHeight: 8,
            containerHeight: fullPage ? '100vh' : '400px'
        }
    };

    const sizes = sizeMap[size];

    // Default lunch welfare icon
    const getIcon = () => {
        if (icon) return icon;
        return <LunchDining sx={{ fontSize: sizes.iconSize }} />;
    };

    // Brown gradient for progress bar
    const gradientColors = `linear-gradient(90deg, ${primaryColor}, ${secondaryColor}, ${primaryLight})`;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: sizes.containerHeight,
                width: '100%',
                gap: 3,
                p: 4,
                position: 'relative'
            }}
        >
            <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    {/* Animated food icon */}
                    <Box
                        sx={{
                            animation: 'bounce-food 2s ease-in-out infinite',
                            '@keyframes bounce-food': {
                                '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                                '50%': { transform: 'translateY(-10px) rotate(5deg)' },
                            },
                        }}
                    >
                        <Avatar
                            sx={{
                                width: sizes.iconSize + 20,
                                height: sizes.iconSize + 20,
                                bgcolor: `${primaryLight}20`,
                                color: primaryColor,
                                boxShadow: `0 4px 15px ${primaryColor}40`,
                                border: `2px solid ${primaryColor}20`
                            }}
                        >
                            {getIcon()}
                        </Avatar>
                    </Box>
                    
                    {/* Animated rings */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -10,
                            left: -10,
                            right: -10,
                            bottom: -10,
                            borderRadius: '50%',
                            border: `2px solid ${secondaryColor}40`,
                            animation: 'ripple 1.5s ease-out infinite',
                            '@keyframes ripple': {
                                '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                                '100%': { transform: 'scale(1.3)', opacity: 0 },
                            },
                        }}
                    />
                    
                    {/* Second ring for more effect */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -5,
                            left: -5,
                            right: -5,
                            bottom: -5,
                            borderRadius: '50%',
                            border: `2px solid ${primaryColor}30`,
                            animation: 'ripple 1.5s ease-out infinite 0.5s',
                            '@keyframes ripple': {
                                '0%': { transform: 'scale(0.9)', opacity: 0.4 },
                                '100%': { transform: 'scale(1.4)', opacity: 0 },
                            },
                        }}
                    />
                </Box>
            </Zoom>
            
            <Fade in={true} timeout={1000}>
                <Box sx={{ textAlign: 'center', maxWidth: 400, width: '100%' }}>
                    <Typography 
                        variant={sizes.fontSize} 
                        sx={{ 
                            color: primaryColor,
                            mb: 2,
                            fontWeight: 600,
                            letterSpacing: '0.5px'
                        }}
                    >
                        {message}
                    </Typography>
                    
                    <LinearProgress 
                        sx={{ 
                            height: sizes.progressHeight, 
                            borderRadius: 4,
                            backgroundColor: `${primaryLight}30`,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: gradientColors,
                                animation: 'shimmer 2s infinite',
                                '@keyframes shimmer': {
                                    '0%': { backgroundPosition: '-200% 0' },
                                    '100%': { backgroundPosition: '200% 0' },
                                },
                                backgroundSize: '200% 100%'
                            }
                        }} 
                    />
                    
                    {/* Animated dots */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 2 }}>
                        {[...Array(3)].map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: size === 'small' ? 8 : 10,
                                    height: size === 'small' ? 8 : 10,
                                    borderRadius: '50%',
                                    backgroundColor: i === 1 ? secondaryColor : primaryColor,
                                    animation: `bounce-dots 1.4s ease-in-out ${i * 0.2}s infinite`,
                                    '@keyframes bounce-dots': {
                                        '0%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
                                        '50%': { transform: 'translateY(-12px)', opacity: 1 },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                    
                    {/* Subtitle message */}
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            mt: 2,
                            color: 'text.secondary',
                            fontSize: '0.7rem'
                        }}
                    >
                        Preparing your lunch welfare experience...
                    </Typography>
                </Box>
            </Fade>

            {/* Decorative background elements */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    opacity: 0.03,
                    zIndex: -1
                }}
            >
                <LunchDining sx={{ fontSize: 150 }} />
            </Box>
            
            <Box
                sx={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    opacity: 0.03,
                    zIndex: -1
                }}
            >
                <Restaurant sx={{ fontSize: 120 }} />
            </Box>
            
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '30%',
                    left: '10%',
                    opacity: 0.02,
                    zIndex: -1,
                    transform: 'rotate(15deg)'
                }}
            >
                <Fastfood sx={{ fontSize: 100 }} />
            </Box>
            
            <Box
                sx={{
                    position: 'absolute',
                    top: '20%',
                    right: '10%',
                    opacity: 0.02,
                    zIndex: -1,
                    transform: 'rotate(-10deg)'
                }}
            >
                <LocalDining sx={{ fontSize: 90 }} />
            </Box>
        </Box>
    );
};

export default LoadingState;